
import commaNumber from 'comma-number'
import analytics from '@/helpers/analytics'
import { partnerLineDisplay } from '@/helpers/currencyDisplay'

export default {
  components: {
    DropdownPartnerItem: () => import('@/components/DropdownPartnerItem.vue')
  },
  props: {
    item: {
      type: Object,
      default: null
    }
  },
  data () {
    return {
      maxShowPartners: 8,
      maxShowItems: 10,
      maxVisibleInlineOffers: 3,
      inlineOffersExpanded: false,
      imageLoadError: false,
      roomsLeft: Math.floor(Math.random() * 13) + 2
    }
  },
  computed: {
    deeplinks () {
      return this.$store.state.deeplinks
    },
    isEmptyRooms () {
      return !this.item.rooms || this.item.rooms.length === 0
    },
    firstRoom () {
      return this.item.rooms ? this.item.rooms[0] : null
    },
    providerLogo () {
      return this.firstRoom ? this.firstRoom.providerLogo : false
    },
    guestRatingFormatted () {
      const rating = parseFloat(this.item.guestRating)
      const validRating = isNaN(rating) ? 0 : rating

      if (validRating === 0) {
        return null
      }

      return Math.min(Math.max(validRating, 0), 10).toFixed(1)
    },
    reviewLabelText () {
      const rating = parseFloat(this.item.guestRating)
      if (isNaN(rating)) {
        return this.$t('hotels.results.reviewFair')
      }
      if (rating >= 9) return this.$t('hotels.results.reviewWonderful')
      if (rating >= 7.5) return this.$t('hotels.results.reviewVeryGood')
      if (rating >= 6) return this.$t('hotels.results.reviewGood')
      if (rating >= 4) return this.$t('hotels.results.reviewOkay')
      return this.$t('hotels.results.reviewFair')
    },
    reviewCountDisplay () {
      const n = this.item.reviewCount
      if (n == null || n === '') return null
      const num = typeof n === 'number' ? n : parseInt(String(n).replace(/\D/g, ''), 10)
      if (Number.isNaN(num) || num <= 0) return null
      return this.$t('hotels.results.reviewsCount', { count: num })
    },
    starRating () {
      const rating = parseInt(this.item.starRating, 10)
      const validRating = Number.isNaN(rating) ? 0 : rating

      if (validRating <= 0) {
        return null
      }

      return Math.min(Math.max(validRating, 1), 5)
    },
    isHighlighted () {
      return this.item.highlighted ?? false
    },
    showBadgeRow () {
      return Boolean(this.item.onlyOneRoomLeft || this.item.freeCancellation || true)
    },
    roomsLeftText () {
      const prefix = this.roomsLeft < 4 ? 'Only ' : ''
      return `${prefix}${this.roomsLeft} rooms left`
    },
    roomsLeftClasses () {
      if (this.roomsLeft >= 7) return 'bg-emerald-100 text-emerald-900'
      if (this.roomsLeft >= 4) return 'bg-yellow-100 text-yellow-900'
      return 'bg-rose-100 text-rose-900'
    },
    minPriceFormatted () {
      if (!this.firstRoom) return ''
      const { amount } = partnerLineDisplay(this.item, this.$store.state)
      return commaNumber(Math.round(amount))
    },
    itemNameFormatted () {
      if (!this.item.name) return ''
      return this.item.name
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ')
    },
    itemAddressFormatted () {
      if (!this.item.address) return ''
      return this.item.address.split(',')[0]
    },
    providerName () {
      return this.firstRoom ? this.firstRoom.provider : null
    },
    cardRoomAternatives () {
      if (!this.item.rooms || !this.item.rooms.length) return []
      return this.item.rooms.slice(1, this.maxShowPartners).map(provider => ({
        ...provider, component: 'DropdownPartnerItem'
      }))
    },
    inlineDropdownItems () {
      return this.cardRoomAternatives
    },
    totalInlineOffersCount () {
      return this.inlineDropdownItems.length
    },
    /** Extra room rows from API beyond what we include in cardRoomAternatives (slice cap) */
    roomsTruncatedCount () {
      const extraRooms = Math.max(0, (this.item.rooms?.length || 0) - 1)
      return Math.max(0, extraRooms - this.cardRoomAternatives.length)
    },
    desktopPreviewCount () {
      const len = this.itemNameFormatted.length
      if (len <= 28) return 2
      if (len < 55) return 1
      return 0
    },
    desktopPreviewOffers () {
      return this.inlineDropdownItems.slice(0, this.desktopPreviewCount)
    },
    hasMoreDesktopOffers () {
      return this.inlineDropdownItems.length > this.desktopPreviewCount
    },
    extraOffersCount () {
      return Math.max(0, this.inlineDropdownItems.length - this.desktopPreviewCount)
    },
    hotelImages () {
      const images = this.item.images
      if (Array.isArray(images) && images.length) return images
      return this.item.image ? [this.item.image] : []
    },
    localCurrency () {
      return this.$store.state.localCurrency
    },
    hotelStayNights () {
      return Math.max(1, this.$store.state.hotelStayNights || 1)
    },
    hotelPriceDisplayMode () {
      return this.$store.state.hotelPriceDisplayMode
    },
    displaySymbol () {
      if (!this.firstRoom) return ''
      return partnerLineDisplay(this.item, this.$store.state).symbol
    }
  },
  watch: {
    'item.uniqueId' () {
      this.inlineOffersExpanded = false
    },
    totalInlineOffersCount (n) {
      if (n <= this.maxVisibleInlineOffers) {
        this.inlineOffersExpanded = false
      }
    }
  },
  methods: {
    roomPriceSymbol (room) {
      return partnerLineDisplay(room, this.$store.state, this.item).symbol
    },
    onImageError () {
      this.imageLoadError = true
    },
    minPriceRoomFormatted (item) {
      const { amount } = partnerLineDisplay(item, this.$store.state, this.item)
      return commaNumber(Math.round(amount))
    },
    handleCardClick () {
      if (this.firstRoom) {
        this.openPartnerDeeplinkUrl(this.firstRoom)
      }
    },
    clickPhoneQuote () {
      analytics.track('meta-unit-phone-click', this)
      window.top.location = `tel:${this.item.phoneQuote.number}`
    },
    partnerClick () {
      const partnerCpc = Number(this.item.cpc).toFixed(2)
      const currency = 'USD'

      if (this.$gtag && this.$store.state.siteConfig.googleConversion) {
        this.$gtag.event('conversion', {
          send_to: this.$store.state.siteConfig.googleConversion,
          value: partnerCpc,
          currency
        })
      }
      if (window.uetq) {
        window.uetq.push('event', 'purchase', {
          revenue_value: partnerCpc,
          currency
        })
      }
      this.fireAdaraEvent(this.item)
    },
    openPartnerDeeplinkUrl (item = this.item) {
      if (!item || !item.partnerDeeplinkUrl) {
        return
      }
      if (item.cpc > 0) {
        this.partnerClick()
      }
      analytics.track('pricelink-click', this)
      console.info('Open partner deeplink URL')
      window.newTab = window.open(item.partnerDeeplinkUrl, '_blank')
    },
    dropdownItemKey (entry, index) {
      return entry.id || entry.uniqueId || entry.providerCode || entry.partnerCode || `${entry.component}-${index}`
    },
    toggleInlineOffersExpanded () {
      this.inlineOffersExpanded = !this.inlineOffersExpanded
    },
    openApiDeeplinkUrls () {
      analytics.track('view-deals-click', this)
      console.info('Open API deeplink URLs')
      const deeplinks = this.item.apiDeeplinkUrls
      window.newTab = window.open('', '_blank')

      if (deeplinks[0]) {
        window.newTab.location.href = deeplinks[0].redirect
      }

      if (deeplinks[1]) {
        window.location.href = deeplinks[1].redirect
      }

      if (deeplinks[2] && window.popUnder) {
        window.popUnder.location.href = deeplinks[2].redirect
        window.popUnder.moveTo(0, 0)
        window.popUnder.resizeTo(screen.width, screen.height)
      }
    },
    fireAdaraEvent (item) {
      if (Object.keys(item).length === 0) return

      const adaraParams = {
        travelProductType: 'Hotel',
        rentalCompany: item.provider.name,
        vehicleModel: item.name
      }
      try {
        window.adara('send', {
          pageType: 'Purchase Item',
          siteLanguage: this.$store.state.language,
          siteCountry: this.$store.state.country,
          ...adaraParams
        })
      } catch (e) {
        console.info('Error firing Adara event')
      }
    }
  }
}
